import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Bell, Pin, BookOpen, Code, Bookmark, Gamepad2, Terminal, ArrowRight, ChevronRight, MessageCircle, Video } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../supabase/client';
import { NoticeItem } from '../types/homepage';

interface SiteConfig {
  site_name: string;
  logo_url: string;
  footer_content: string;
  contact_phone: string;
  contact_email: string;
  icp_info: string;
}

interface NoticeFromDB {
  id: number;
  title: string;
  summary: string;
  link: string;
  is_pinned: boolean;
  is_highlighted: boolean;
  publish_time: string;
  link_type: 'internal' | 'external';
}

// 蓝天白云渐变背景样式
const skyStyles = `
  .sky-gradient {
    background: linear-gradient(180deg, 
      #E3F2FD 0%, 
      #BBDEFB 15%,
      #E3F2FD 35%,
      #F5FBFF 60%,
      #FFFFFF 100%
    );
  }
  .sky-card {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 24px;
    border: 1px solid rgba(33, 150, 243, 0.1);
    box-shadow: 0 8px 32px rgba(33, 150, 243, 0.08);
    transition: all 0.3s ease;
  }
  .sky-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(33, 150, 243, 0.15);
    border-color: rgba(33, 150, 243, 0.2);
  }
  .sky-btn {
    background: linear-gradient(135deg, #2196F3 0%, #03A9F4 100%);
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(33, 150, 243, 0.3);
    transition: all 0.3s ease;
  }
  .sky-btn:hover {
    box-shadow: 0 6px 20px rgba(33, 150, 243, 0.4);
    transform: translateY(-2px);
  }
  .glass-card {
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.8);
  }
  .stat-blue { background: linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%); }
  .stat-cyan { background: linear-gradient(135deg, #E0F7FA 0%, #B2EBF2 100%); }
  .stat-light-blue { background: linear-gradient(135deg, #E1F5FE 0%, #B3E5FC 100%); }
  .stat-sky { background: linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%); }
`;

export default function Home() {
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null);
  const [notices, setNotices] = useState<NoticeItem[]>([]);

  useEffect(() => {
    fetchData();
    // 注入蓝天白云样式
    const styleEl = document.createElement('style');
    styleEl.textContent = skyStyles;
    document.head.appendChild(styleEl);
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  const fetchData = async () => {
    try {
      const [configResult, noticesResult] = await Promise.all([
        supabase.from('site_config').select('*').eq('id', 'default').maybeSingle(),
        supabase.from('homepage_notices').select('*').eq('is_visible', true).is('deleted_at', null).order('is_pinned', { ascending: false }).order('publish_time', { ascending: false }).limit(6),
      ]);

      if (configResult.data) {
        setSiteConfig(configResult.data as SiteConfig);
      }

      if (noticesResult.data) {
        const formattedNotices: NoticeItem[] = (noticesResult.data as NoticeFromDB[]).map(n => ({
          id: String(n.id),
          title: n.title,
          summary: n.summary,
          link: n.link,
          linkType: n.link_type,
          isPinned: n.is_pinned,
          isHighlighted: n.is_highlighted,
          publishTime: n.publish_time,
        }));
        setNotices(formattedNotices);
      }
    } catch (error) {
      // 静默处理错误
    }
  };

  return (
    <div className="min-h-screen sky-gradient">
 {/* Hero Section - 左右两栏布局 */}
 <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16 lg:py-20">
 <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
 {/* 左侧：品牌内容 */}
 <motion.div
 initial={{ opacity: 0, x: -30 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ duration: 0.6 }}
 className="space-y-6"
 >
 {/* 标签 */}
 <motion.div
 initial={{ opacity: 0, y: 10 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.1 }}
 className="inline-flex items-center gap-2 bg-blue-50 rounded-full px-4 py-2"
 >
 <Sparkles className="w-4 h-4 text-blue-500" />
 <span className="text-sm font-medium text-blue-600">专注青少年编程等级考试</span>
 </motion.div>

 {/* 主标题 */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.2 }}
 >
 <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-800 leading-tight mb-2">
 编程刷题
 </h1>
 <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight">
 <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-500">
 成就科技特长生
 </span>
 </h1>
 </motion.div>

 {/* 副标题 */}
 <motion.p
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.3 }}
 className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl"
 >
 覆盖电子学会、CCF GESP等主流编程等级考试，AI智能判题，真题模拟，助力学员高效备考
 </motion.p>

 {/* 按钮组 */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.4 }}
 className="flex flex-wrap gap-3 md:gap-4"
 >
 <Link to="/exam-categories">
 <motion.button
 className="sky-btn text-white px-6 md:px-8 py-3 md:py-4 font-bold text-base md:text-lg flex items-center gap-2"
 whileHover={{ scale: 1.02 }}
 whileTap={{ scale: 0.98 }}
 >
 <span>🚀</span> 开始刷题
 </motion.button>
 </Link>
 <Link to="/register">
 <motion.button
 className="bg-white text-slate-700 px-6 md:px-8 py-3 md:py-4 rounded-2xl font-bold text-base md:text-lg shadow-lg hover:shadow-xl transition-all border border-blue-100"
 whileHover={{ scale: 1.02 }}
 whileTap={{ scale: 0.98 }}
 >
 了解更多
 </motion.button>
 </Link>
 </motion.div>

          </motion.div>

 {/* 右侧：公告栏 */}
 {notices.length > 0 && (
 <motion.div
 initial={{ opacity: 0, x: 30 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ duration: 0.6, delay: 0.2 }}
 className="lg:pl-8"
 >
 <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/60 shadow-xl shadow-slate-200/50 overflow-hidden">
 {/* 公告栏头部 */}
 <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-amber-50/50 to-orange-50/30">
 <div className="flex items-center gap-2">
 <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
 <Bell className="w-4 h-4 text-amber-600" />
 </div>
 <h3 className="font-bold text-slate-800">最新公告</h3>
 </div>
 <Link
 to="/notices"
 className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 transition-colors"
 >
 查看全部
 <ChevronRight className="w-4 h-4" />
 </Link>
 </div>

 {/* 公告列表 */}
 <div className="max-h-[320px] overflow-y-auto">
 {notices.slice(0, 5).map((notice) => (
 <Link
 key={notice.id}
 to={notice.link || `/notice/${notice.id}`}
 className="group block px-6 py-4 hover:bg-slate-50/80 transition-colors border-b border-slate-100 last:border-b-0"
 >
 <div className="flex items-start gap-3">
 <div className="flex-shrink-0 mt-0.5">
 {notice.isPinned ? (
 <Pin className="w-4 h-4 text-amber-500" />
 ) : notice.isHighlighted ? (
 <Sparkles className="w-4 h-4 text-purple-500" />
 ) : (
 <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5" />
 )}
 </div>
 <div className="flex-1 min-w-0">
 <h4
 className={`text-sm font-medium line-clamp-1 group-hover:text-blue-600 transition-colors ${
 notice.isHighlighted ? 'text-slate-900' : 'text-slate-700'
 }`}
 >
 {notice.isPinned && (
 <span className="text-amber-600 mr-1">[置顶]</span>
 )}
 {notice.title}
 </h4>
 {notice.summary && (
 <p className="text-xs text-slate-500 mt-1 line-clamp-1">
 {notice.summary}
 </p>
 )}
 {notice.publishTime && (
 <p className="text-xs text-slate-400 mt-1.5">
 {new Date(notice.publishTime).toLocaleDateString('zh-CN', {
 month: 'short',
 day: 'numeric',
 })}
 </p>
 )}
 </div>
 <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-400 flex-shrink-0 mt-0.5 transition-colors" />
 </div>
 </Link>
 ))}
 </div>
 </div>
 </motion.div>
 )}
 </div>
 </section>
      {/* 核心功能 */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-8 md:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          核心功能
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {[
            { icon: BookOpen, title: '真题题库', desc: '5000+真题，覆盖电子学会、CCF等权威考试', color: 'from-blue-400 to-blue-600', emoji: '📚' },
            { icon: Code, title: '在线编程', desc: 'Scratch、Python、C++，随时随地写代码', color: 'from-cyan-400 to-cyan-600', emoji: '💻' },
            { icon: Bookmark, title: '错题本', desc: '智能收录错题，针对性练习提升', color: 'from-sky-400 to-sky-600', emoji: '📝' },
          ].map((item, index) => (
            <motion.div
              key={item.title}
              className="sky-card p-6 md:p-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className={`w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mb-4 md:mb-6`}>
                <span className="text-white text-xl md:text-2xl">{item.emoji}</span>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-slate-800 mb-2 md:mb-3">{item.title}</h3>
              <p className="text-slate-600 text-sm md:text-base">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 编程语言 */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-8 md:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          编程语言
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {[
            { icon: Gamepad2, title: 'Scratch 编程', desc: '图形化编程入门，适合6-12岁', color: 'from-orange-400 to-amber-500', emoji: '🎮' },
            { icon: Code, title: 'Python 编程', desc: '代码编程基础，适合10-16岁', color: 'from-blue-400 to-cyan-500', emoji: '🐍' },
            { icon: Terminal, title: 'C++ 编程', desc: '竞赛进阶，适合12-18岁', color: 'from-red-400 to-rose-500', emoji: '⚡' },
          ].map((item, index) => (
            <motion.div
              key={item.title}
              className="sky-card p-6 md:p-8 text-center cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className={`w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6`}>
                <span className="text-white text-2xl md:text-3xl">{item.emoji}</span>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-slate-800 mb-1 md:mb-2">{item.title}</h3>
              <p className="text-slate-600 text-sm md:text-base">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <motion.div
          className="glass-card rounded-3xl p-8 md:p-12 text-center shadow-lg shadow-blue-100/50"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3 md:mb-4">开始你的编程之旅</h2>
          <p className="text-base md:text-lg text-slate-600 mb-6 md:mb-8">立即注册，免费体验全部功能</p>
          <Link to="/register">
            <motion.button
              className="sky-btn text-white px-10 md:px-12 py-3 md:py-4 rounded-2xl font-bold text-base md:text-lg"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              免费注册
            </motion.button>
          </Link>
        </motion.div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}

// 页脚组件
function Footer() {
  const [footerConfig, setFooterConfig] = useState({
    site_name: '编程优题库',
    site_desc: '专注青少年编程教育，助力科技特长生成长',
    copyright: '© 2024 编程优题库 版权所有',
    icp_info: '苏ICP备2026023699号',
    contact_email: 'kefu_51kc@foxmail.com',
    quick_links: [
      { name: '刷题平台', path: '/exam-categories' },
      { name: '帮助中心', path: '/help' },
      { name: '管理员登录', path: '/admin' },
    ],
  });

  useEffect(() => {
    loadFooterConfig();
  }, []);

  const loadFooterConfig = async () => {
    try {
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/footer-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get' }),
      });
      const result = await response.json();
      if (result.success && result.data) {
        setFooterConfig({
          ...footerConfig,
          ...result.data,
          quick_links: result.data.quick_links || footerConfig.quick_links,
        });
      }
    } catch (error) {
      // 静默处理错误
    }
  };

  return (
    <footer className="bg-white/80 backdrop-blur-md border-t border-blue-100/50 py-8 md:py-12">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-slate-600 mb-6 md:mb-8">
          <div className="col-span-2 md:col-span-1">
            <h4 className="text-slate-800 font-bold mb-3">{footerConfig.site_name}</h4>
            <p className="text-sm">{footerConfig.site_desc}</p>
          </div>
          <div>
            <h4 className="text-slate-800 font-bold mb-3">快速链接</h4>
            <ul className="space-y-2 text-sm">
              {footerConfig.quick_links.map((link: any, index: number) => (
                <li key={index}>
                  <Link to={link.path} className="hover:text-blue-600 transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-slate-800 font-bold mb-3">联系我们</h4>
            <p className="text-sm">{footerConfig.contact_email}</p>
          </div>
          <div>
            <h4 className="text-slate-800 font-bold mb-3">关注我们</h4>
            <div className="flex gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center hover:bg-blue-100 cursor-pointer transition-colors">
                <MessageCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center hover:bg-blue-100 cursor-pointer transition-colors">
                <Video className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-blue-100 pt-6 md:pt-8 text-center text-slate-500 text-xs md:text-sm">
          <p>{footerConfig.copyright} · {footerConfig.icp_info}</p>
        </div>
      </div>
    </footer>
  );
}
